import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Category } from "~/api/model";
import {
  getListOrganizationCategoriesQueryKey,
  useDeleteOrganizationCategory,
} from "~/api/organization/organization";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

interface DeleteCategoryAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  categoryId: string;
  fallbackCategory: Category | null;
}

export const DeleteCategoryAlertDialog: FC<DeleteCategoryAlertDialogProps> = ({
  open,
  setOpen,
  categoryId,
  fallbackCategory,
}) => {
  if (!fallbackCategory) {
    throw new Error("Fallback category is missing.");
  }
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: deleteCategory } = useDeleteOrganizationCategory();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();

  const onDeleteCategory = () => {
    trackEvent({
      eventName: "app/doc_list/delete_category_modal/delete.button/click",
    });

    const queryKey = getListOrganizationCategoriesQueryKey(organizationId);

    // Save previous data for rollback
    const previousData = queryClient.getQueryData<Category[]>(queryKey);

    // Optimistic update: immediately remove category from cache
    queryClient.setQueryData(queryKey, (oldData: Category[] | undefined) => {
      if (!oldData) return oldData;
      return oldData.filter((c) => c.id.toString() !== categoryId);
    });

    // Close dialog immediately
    setOpen(false);

    // Delete in background with error rollback
    deleteCategory({ organizationId, categoryId }).catch(() => {
      queryClient.setQueryData(queryKey, previousData);
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "settings.deleteCategoryAlertDialog.deleteError",
        }),
      });
    });
  };

  const handleCancel = () => {
    trackEvent({
      eventName: "app/doc_list/delete_category_modal/cancel.button/click",
    });
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="settings.deleteCategoryAlertDialog.title"
              defaultMessage="Delete Category"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="settings.deleteCategoryAlertDialog.description"
              defaultMessage={
                "Are you sure you want to delete this category? Related documents will become {categoryName}."
              }
              values={{
                categoryName: <strong>{fallbackCategory.name}</strong>,
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button variant="destructive" onClick={onDeleteCategory}>
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
